import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import styles from './toast.module.css';

interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'error' | 'success';
  action?: { label: string; onClick: () => void };
}

const ToastContext = createContext<(text: string, opts?: { tone?: Toast['tone']; action?: Toast['action'] }) => void>(() => {});

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((text: string, opts: { tone?: Toast['tone']; action?: Toast['action'] } = {}) => {
    const id = nextId++;
    setToasts((t) => [...t.slice(-2), { id, text, tone: opts.tone ?? 'info', ...(opts.action ? { action: opts.action } : {}) }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), opts.action ? 5000 : 2800);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className={styles.region} role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`${styles.toast} ${styles[t.tone]}`}>
            <span>{t.text}</span>
            {t.action && (
              <button className={styles.action} onClick={t.action.onClick}>
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
